import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Layout from "../../../component/layout";
import { makeRequest2 } from "../../../../../utils/makeRequest";
import { toastify } from "../../../../../utils/toast";
import { useFacilityStore } from "../../../../../app/z-store/store";
import { getBudget } from "../../../../../utils/urls";

const ViewBudget = () => {
  const navigate = useNavigate();
  const { budgetId } = useParams();
  const selectedFacilityId = useFacilityStore((state) => state.facilityId);

  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('ViewBudget useEffect triggered');
    console.log('selectedFacilityId:', selectedFacilityId);
    console.log('budgetId:', budgetId);
    console.log('getBudget URL base:', getBudget);
    
    if (selectedFacilityId && budgetId) {
      fetchBudgetDetails();
    } else {
      console.log('Missing required parameters:');
      console.log('- selectedFacilityId:', selectedFacilityId);
      console.log('- budgetId:', budgetId);
      setLoading(false);
    }
  }, [selectedFacilityId, budgetId]);

  const fetchBudgetDetails = async () => {
    try {
      setLoading(true);
      
      // Construct the full URL
      const fullUrl = `${getBudget}/${selectedFacilityId}/${budgetId}`;
      console.log('🚀 Making API call to:', fullUrl);
      console.log('🔧 Request method: GET');
      
      const response = await makeRequest2(fullUrl, 'GET');
      
      console.log('📨 API Response received:', response);
      console.log('📊 Response success:', response?.success);
      console.log('📄 Response budget:', response?.budget);

      if (response.success && response.budget) {
        console.log('✅ Setting budget data:', response.budget);
        setBudget(response.budget);
      } else {
        console.log('❌ API call failed or no budget data');
        console.log('Error:', response.error);
        console.log('Full response:', response);
        toastify(response.error || 'Failed to fetch budget details', 'error');
      }
    } catch (error) {
      console.log('💥 Exception caught in fetchBudgetDetails:');
      console.error('Error details:', error);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      toastify('Error fetching budget details', 'error');
    } finally {
      console.log('🏁 Setting loading to false');
      setLoading(false);
    }
  };

  // Add debug info to check what's happening
  console.log('🔍 Current component state:');
  console.log('- loading:', loading);
  console.log('- budget:', budget);
  console.log('- selectedFacilityId:', selectedFacilityId);
  console.log('- budgetId:', budgetId);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'KES'
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Calculate remaining budget
  const calculateRemaining = () => {
    if (!budget) return 0;
    return (budget.allocation || 0) - (budget.used || 0);
  };

  // Calculate usage percentage
  const calculateUsagePercentage = () => {
    if (!budget || !budget.allocation) return 0;
    return Math.round(((budget.used || 0) / budget.allocation) * 100);
  };

  // Get status badge style
  const getStatusStyle = (status) => {
    const statusStyles = {
      approved: 'bg-success',
      pending: 'bg-warning',
      in_progress: 'bg-info',
      rejected: 'bg-danger'
    };
    return statusStyles[status] || 'bg-secondary';
  };

  // Get approval step status
  const getStepStatus = (step, currentStep) => {
    if (step.stepNumber < currentStep) return 'completed';
    if (step.stepNumber === currentStep) return 'current';
    return 'pending';
  };

  // If no facility is selected, show a prompt
  if (!selectedFacilityId) {
    console.log('⚠️ No facility selected, showing prompt');
    return (
      <Layout>
        <div className="card">
          <div className="card-body text-center">
            <div className="alert alert-warning">
              <h6>No Facility Selected</h6>
              <p>Please select a facility to view budget details.</p>
              <p className="small text-muted">Current facility ID: {selectedFacilityId || 'null'}</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!budgetId) {
    console.log('⚠️ No budget ID found in URL parameters');
    return (
      <Layout>
        <div className="card">
          <div className="card-body text-center">
            <div className="alert alert-danger">
              <h6>Invalid Budget ID</h6>
              <p>No budget ID found in the URL parameters.</p>
              <p className="small text-muted">Budget ID: {budgetId || 'null'}</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Debug Information Card (Remove in production) */}
      <div className="card mb-3 border-info">
        <div className="card-header bg-info text-white">
          <h6 className="mb-0">🔧 Debug Information (Remove in production)</h6>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <strong>URL Parameters:</strong>
              <ul className="mb-0">
                <li>Budget ID: {budgetId || 'null'}</li>
                <li>Facility ID: {selectedFacilityId || 'null'}</li>
                <li>API URL: {getBudget ? `${getBudget}/${selectedFacilityId}/${budgetId}` : 'getBudget is undefined'}</li>
              </ul>
            </div>
            <div className="col-md-6">
              <strong>Component State:</strong>
              <ul className="mb-0">
                <li>Loading: {loading ? 'true' : 'false'}</li>
                <li>Budget Data: {budget ? 'Available' : 'null'}</li>
                <li>API Called: Check console logs</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Page Header & Breadcrumbs */}
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/facility/expense_management/budget">
                    Budget Management
                  </Link>
                </li>
                <li className="breadcrumb-item">View Budget</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Main content card */}
      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <div>
                <Link to="/facility/expense_management/budget">
                  <i className="ti ti-arrow-narrow-left"></i> Back
                </Link>
              </div>
              <div>
                <button
                  className="btn btn-primary"
                  onClick={() => navigate(`/facility/budget_management/edit/${budgetId}`)}
                  disabled={budget?.approvalStatus !== 'pending' || loading}
                >
                  Edit Budget
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="card">
              <div className="card-body text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading budget details...</span>
                </div>
                <p className="mt-2">Fetching budget details...</p>
                <p className="small text-muted">
                  URL: {getBudget ? `${getBudget}/${selectedFacilityId}/${budgetId}` : 'URL not constructed'}
                </p>
              </div>
            </div>
          ) : budget ? (
            <>
              {/* Budget Overview Card */}
              <div className="card">
                <div className="card-header">
                  <h5>Budget Overview</h5>
                </div>
                <div className="card-body">
                  <div className="row mb-4">
                    <div className="col-md-6">
                      <div className="card">
                        <div className="card-header bg-light">
                          <h6 className="mb-0">Basic Information</h6>
                        </div>
                        <div className="card-body">
                          <table className="table table-borderless">
                            <tbody>
                              <tr>
                                <td className="fw-bold">Budget Name:</td>
                                <td>{budget.budgetName || 'N/A'}</td>
                              </tr>
                              <tr>
                                <td className="fw-bold">Department:</td>
                                <td>{budget.department || 'N/A'}</td>
                              </tr>
                              <tr>
                                <td className="fw-bold">Created By:</td>
                                <td>{budget.userName || 'N/A'}</td>
                              </tr>
                              <tr>
                                <td className="fw-bold">Status:</td>
                                <td>
                                  <span className={`badge ${getStatusStyle(budget.approvalStatus)}`}>
                                    {budget.approvalStatus ? 
                                      budget.approvalStatus.charAt(0).toUpperCase() + 
                                      budget.approvalStatus.slice(1).replace('_', ' ') : 'N/A'}
                                  </span>
                                </td>
                              </tr>
                              <tr>
                                <td className="fw-bold">Allow Overruns:</td>
                                <td>
                                  <span className={`badge ${budget.overruns ? 'bg-success' : 'bg-secondary'}`}>
                                    {budget.overruns ? 'Yes' : 'No'}
                                  </span>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="card">
                        <div className="card-header bg-light">
                          <h6 className="mb-0">Budget Period</h6>
                        </div>
                        <div className="card-body">
                          <table className="table table-borderless">
                            <tbody>
                              <tr>
                                <td className="fw-bold">Start Date:</td>
                                <td>{budget.startDate ? formatDate(budget.startDate) : 'N/A'}</td>
                              </tr>
                              <tr>
                                <td className="fw-bold">End Date:</td>
                                <td>{budget.endDate ? formatDate(budget.endDate) : 'N/A'}</td>
                              </tr>
                              <tr>
                                <td className="fw-bold">Created:</td>
                                <td>{budget.createdAt ? formatDate(budget.createdAt) : 'N/A'}</td>
                              </tr>
                              <tr>
                                <td className="fw-bold">Last Updated:</td>
                                <td>{budget.updatedAt ? formatDate(budget.updatedAt) : 'N/A'}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Budget Financial Summary */}
                  <div className="row mb-4">
                    <div className="col-md-12">
                      <div className="card">
                        <div className="card-header bg-light">
                          <h6 className="mb-0">Financial Summary</h6>
                        </div>
                        <div className="card-body">
                          <div className="row">
                            <div className="col-md-3">
                              <div className="text-center p-3 border rounded">
                                <h4 className="text-primary mb-1">{formatCurrency(budget.allocation)}</h4>
                                <p className="mb-0 text-muted">Total Allocation</p>
                              </div>
                            </div>
                            <div className="col-md-3">
                              <div className="text-center p-3 border rounded">
                                <h4 className="text-warning mb-1">{formatCurrency(budget.used || 0)}</h4>
                                <p className="mb-0 text-muted">Amount Used</p>
                              </div>
                            </div>
                            <div className="col-md-3">
                              <div className="text-center p-3 border rounded">
                                <h4 className="text-success mb-1">{formatCurrency(calculateRemaining())}</h4>
                                <p className="mb-0 text-muted">Remaining</p>
                              </div>
                            </div>
                            <div className="col-md-3">
                              <div className="text-center p-3 border rounded">
                                <h4 className="text-info mb-1">{calculateUsagePercentage()}%</h4>
                                <p className="mb-0 text-muted">Usage</p>
                              </div>
                            </div>
                          </div>
                          
                          {/* Progress Bar */}
                          <div className="mt-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <span className="text-muted">Budget Usage</span>
                              <span className="fw-bold">{calculateUsagePercentage()}%</span>
                            </div>
                            <div className="progress" style={{ height: '10px' }}>
                              <div 
                                className={`progress-bar ${calculateUsagePercentage() > 90 ? 'bg-danger' : 
                                  calculateUsagePercentage() > 75 ? 'bg-warning' : 'bg-success'}`}
                                role="progressbar" 
                                style={{ width: `${Math.min(calculateUsagePercentage(), 100)}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Approval Workflow */}
                  {budget.approvals && budget.approvals.length > 0 && (
                    <div className="row">
                      <div className="col-md-12">
                        <div className="card">
                          <div className="card-header bg-light">
                            <h6 className="mb-0">Approval Workflow</h6>
                          </div>
                          <div className="card-body">
                            <div className="row">
                              {budget.approvals.map((step, index) => (
                                <div key={index} className="col-md-4 mb-3">
                                  <div className={`card h-100 ${
                                    getStepStatus(step, budget.currentStep) === 'completed' ? 'border-success' :
                                    getStepStatus(step, budget.currentStep) === 'current' ? 'border-warning' : 'border-light'
                                  }`}>
                                    <div className="card-header d-flex justify-content-between align-items-center">
                                      <h6 className="mb-0">Step {step.stepNumber}: {step.stepName}</h6>
                                      <span className={`badge ${
                                        getStepStatus(step, budget.currentStep) === 'completed' ? 'bg-success' :
                                        getStepStatus(step, budget.currentStep) === 'current' ? 'bg-warning' : 'bg-secondary'
                                      }`}>
                                        {getStepStatus(step, budget.currentStep) === 'completed' ? 'Completed' :
                                         getStepStatus(step, budget.currentStep) === 'current' ? 'Current' : 'Pending'}
                                      </span>
                                    </div>
                                    <div className="card-body">
                                      {step.approvers && step.approvers.length > 0 ? (
                                        <div className="table-responsive">
                                          <table className="table table-sm">
                                            <thead>
                                              <tr>
                                                <th>Approver</th>
                                                <th>Status</th>
                                                <th>Date</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {step.approvers.map((approver, approverIndex) => (
                                                <tr key={approverIndex}>
                                                  <td>{approver.userId?.fullName || approver.userId || 'N/A'}</td>
                                                  <td>
                                                    <span className={`badge ${
                                                      approver.status === 'approved' ? 'bg-success' :
                                                      approver.status === 'rejected' ? 'bg-danger' : 'bg-warning'
                                                    }`}>
                                                      {approver.status?.charAt(0).toUpperCase() + approver.status?.slice(1)}
                                                    </span>
                                                  </td>
                                                  <td>
                                                    {approver.actionDate ? 
                                                      new Date(approver.actionDate).toLocaleDateString() : 'N/A'}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      ) : (
                                        <p className="text-muted mb-0">No approvers assigned</p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Comments Section */}
                  {budget.approvals && budget.approvals.some(step => 
                    step.approvers.some(approver => approver.comments)
                  ) && (
                    <div className="row">
                      <div className="col-md-12">
                        <div className="card">
                          <div className="card-header bg-light">
                            <h6 className="mb-0">Approval Comments</h6>
                          </div>
                          <div className="card-body">
                            {budget.approvals.map((step, stepIndex) =>
                              step.approvers
                                .filter(approver => approver.comments)
                                .map((approver, approverIndex) => (
                                  <div key={`${stepIndex}-${approverIndex}`} className="border-start border-3 border-info ps-3 mb-3">
                                    <div className="d-flex justify-content-between align-items-start">
                                      <div>
                                        <h6 className="mb-1">
                                          {approver.userId?.fullName || approver.userId || 'Unknown'} 
                                          <small className="text-muted ms-2">
                                            (Step {step.stepNumber}: {step.stepName})
                                          </small>
                                        </h6>
                                        <p className="mb-1">{approver.comments}</p>
                                        <small className="text-muted">
                                          {approver.actionDate ? 
                                            new Date(approver.actionDate).toLocaleString() : 'No date'}
                                        </small>
                                      </div>
                                      <span className={`badge ${
                                        approver.status === 'approved' ? 'bg-success' :
                                        approver.status === 'rejected' ? 'bg-danger' : 'bg-warning'
                                      }`}>
                                        {approver.status?.charAt(0).toUpperCase() + approver.status?.slice(1)}
                                      </span>
                                    </div>
                                  </div>
                                ))
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="card">
              <div className="card-body text-center">
                <div className="alert alert-warning">
                  <h6>Budget Not Found</h6>
                  <p>Budget not found or unable to fetch details.</p>
                  <p className="small text-muted">
                    Attempted URL: {getBudget ? `${getBudget}/${selectedFacilityId}/${budgetId}` : 'URL could not be constructed'}
                  </p>
                  <button 
                    className="btn btn-primary mt-2" 
                    onClick={fetchBudgetDetails}
                    disabled={loading}
                  >
                    Retry
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ViewBudget;